import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  FlaskConical, Loader2, Play, FolderPlus, Mail, MailX, Lock, ShieldAlert, AlertTriangle,
} from 'lucide-react';

const WayaSimulador = () => {
  const [cuentas, setCuentas] = useState([]);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [cvu, setCvu] = useState('');
  const [tipo, setTipo] = useState('preventivo');
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCuentas = useCallback(async () => {
    setLoadingCuentas(true);
    try {
      const r = await api.get('/waya-movimientos/alertas/agrupado', { params: { por: 'cvu', estado: 'PENDIENTE', limit: 1000 } });
      const items = (r.data?.grupos || r.data?.items || []).filter(g => (g.alertados_count || g.total_count || 0) > 0);
      setCuentas(items);
    } catch { setCuentas([]); }
    finally { setLoadingCuentas(false); }
  }, []);

  useEffect(() => { fetchCuentas(); }, [fetchCuentas]);

  const simular = async () => {
    if (!cvu) { toast.error('Elegí una cuenta'); return; }
    setLoading(true); setSim(null);
    try {
      const r = await api.post('/waya-movimientos/simular', { cvu, tipo, buscar_waya: false });
      setSim(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo simular'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="waya-simulador">
      <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 flex items-center gap-2 text-violet-800 text-sm">
        <FlaskConical size={16} className="shrink-0" />
        <span><b>Simulación</b> — mostramos cómo quedaría el caso y el email que se enviaría. <b>No se guarda ni ejecuta nada.</b></span>
      </div>

      <Card className="p-4 bg-white">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-slate-500 mb-1 block">Cuenta con alertas pendientes</label>
            <Select value={cvu} onValueChange={setCvu}>
              <SelectTrigger data-testid="waya-sim-cuenta" className="h-9">
                <SelectValue placeholder={loadingCuentas ? 'Cargando…' : 'Elegí una cuenta'} />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {cuentas.map((g, i) => (
                  <SelectItem key={`${g.cvu_mov}-${i}`} value={g.cvu_mov} data-testid={`waya-sim-cuenta-opt-${i}`}>
                    {(g.nombre || 'Sin nombre')} · …{String(g.cvu_mov || '').slice(-6)} ({g.alertados_count || 0} alerta/s)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <label className="text-xs text-slate-500 mb-1 block">Acción a simular</label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="waya-sim-tipo" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="preventivo">Bloqueo preventivo</SelectItem>
                <SelectItem value="definitivo">Cierre definitivo (baja de CVU)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={simular} disabled={loading || !cvu} className="bg-violet-600 hover:bg-violet-700 text-white h-9" data-testid="waya-sim-run">
            {loading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Play size={15} className="mr-1.5" />} Simular
          </Button>
        </div>
      </Card>

      {sim && sim.casos?.map((c, i) => (
        <div key={i} className="grid md:grid-cols-2 gap-3" data-testid={`waya-sim-resultado-${i}`}>
          {/* Caso simulado */}
          <Card className="p-4 bg-white border-l-4 border-l-violet-500">
            <div className="flex items-center gap-2 mb-2 text-slate-800">
              <FolderPlus size={16} className="text-violet-600" />
              <h3 className="font-semibold text-sm">Caso que se generaría</h3>
            </div>
            <div className="text-sm font-medium text-slate-900">{c.titulo}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <Badge className="bg-slate-800 text-white">{sim.accion_label}</Badge>
              <Badge className="bg-slate-100 text-slate-600">Estado: {c.estado}</Badge>
              <Badge className="bg-slate-100 text-slate-600">Prioridad: {c.prioridad}</Badge>
            </div>
            <div className="mt-3 text-xs text-slate-600 space-y-1">
              <div><span className="text-slate-400">Cliente:</span> {c.nombre || '—'}</div>
              <div className="font-mono"><span className="text-slate-400 font-sans">CUIT:</span> {c.cuit || '—'}</div>
              <div className="font-mono"><span className="text-slate-400 font-sans">Cuentas ({c.total_cuentas}):</span> {(c.cuentas || []).join(', ')}</div>
            </div>
            <div className="mt-3">
              <div className="text-[11px] uppercase text-slate-400 mb-1">Alertas ({c.total_alertas})</div>
              <div className="max-h-40 overflow-auto space-y-1">
                {(c.alertas || []).map((a, j) => (
                  <div key={j} className="text-[11px] bg-slate-50 border border-slate-100 rounded px-2 py-1 text-slate-600">
                    <b>{a.tipo}</b> — {a.descripcion}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-2">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              En Waya: {sim.waya_accion}
            </div>
          </Card>

          {/* Email simulado */}
          <Card className="p-4 bg-white border-l-4 border-l-sky-500">
            <div className="flex items-center gap-2 mb-2 text-slate-800">
              {c.email?.enviaria ? <Mail size={16} className="text-sky-600" /> : <MailX size={16} className="text-slate-400" />}
              <h3 className="font-semibold text-sm">Email que se enviaría</h3>
            </div>
            {c.email?.enviaria ? (
              <>
                <div className="text-xs text-slate-600 mb-1"><span className="text-slate-400">Para:</span> <b>{c.email.to_email}</b></div>
                <div className="text-xs text-slate-600 mb-2"><span className="text-slate-400">Asunto:</span> {c.email.asunto}</div>
                <pre className="text-[11px] whitespace-pre-wrap bg-slate-50 border border-slate-100 rounded p-2 text-slate-700 max-h-56 overflow-auto font-sans">{c.email.contenido}</pre>
              </>
            ) : (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded p-3">
                No hay destinatario en el padrón{c.email?.source ? '' : ''}. En producción se buscaría el email en Waya antes de enviar.
              </div>
            )}
          </Card>
        </div>
      ))}

      {sim && (!sim.casos || sim.casos.length === 0) && (
        <Card className="p-6 bg-white text-center text-slate-400 text-sm">No hay alertas para simular en esa cuenta.</Card>
      )}
    </div>
  );
};

export default WayaSimulador;
